import { DollarSign } from 'lucide-react'
import { fm } from '@/lib/helpers'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'

interface ObraFinanceiro {
  id: string
  nome: string
  valorContrato: number
  avanco: number
}

interface Props {
  obras: ObraFinanceiro[]
  valorComprasAberto: number
}

export default function DashboardFinanceiro({ obras, valorComprasAberto }: Props) {
  const totalContrato = obras.reduce((s, o) => s + (o.valorContrato || 0), 0)
  const obrasComValor = obras.filter(o => o.valorContrato > 0)

  return (
    <TooltipProvider>
      <div className="rounded-xl p-4 border" style={{ background: 'hsl(var(--bg2))', borderColor: 'hsl(var(--line))' }}>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign size={14} className="text-primary" />
          <span className="text-sm font-bold text-foreground">Visão Financeira</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[10px] text-muted-foreground cursor-help ml-1">ⓘ</span>
            </TooltipTrigger>
            <TooltipContent><p className="text-xs max-w-[200px]">Baseado no valor de contrato cadastrado em cada obra e nas listas de compras do estoque.</p></TooltipContent>
          </Tooltip>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-lg" style={{ background: 'hsl(var(--bg3))' }}>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Carteira Total</div>
            <div className="text-lg font-extrabold text-foreground">{fm(totalContrato)}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'hsl(var(--bg3))' }}>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Compras em Aberto</div>
            <div className="text-lg font-extrabold" style={{ color: valorComprasAberto > 0 ? 'hsl(var(--amber))' : 'hsl(var(--emerald))' }}>
              {fm(valorComprasAberto)}
            </div>
          </div>
        </div>

        {obrasComValor.length > 0 && (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Custo por % de Avanço</div>
            {obrasComValor.map(o => {
              const custoPorPct = o.avanco > 0 ? o.valorContrato / o.avanco : null
              return (
                <div key={o.id} className="flex items-center gap-2 text-xs">
                  <span className="text-foreground truncate flex-1 max-w-[140px]">{o.nome}</span>
                  <span className="text-muted-foreground">{o.avanco}%</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-bold text-foreground cursor-help">
                        {custoPorPct ? fm(custoPorPct) + '/1%' : '—'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Valor do contrato dividido pelo avanço físico atual</p></TooltipContent>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        )}

        {obrasComValor.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">Cadastre o valor do contrato nas obras para ver métricas financeiras</p>
        )}
      </div>
    </TooltipProvider>
  )
}
